import type React from "react"

import { twMerge } from "tailwind-merge"

import { Spinner } from "@/components/flow/icons/Spinner"

type ButtonVariant = "primary" | "inverted" | "hero" | "disabled"

interface ButtonProps {
  children?: React.ReactNode
  onClick?: () => void
  variant?: ButtonVariant
  disabled?: boolean
  className?: string
  loading?: boolean
  "data-testid"?: string
}

const baseClasses = "py-4 px-8 rounded-full text-lg transition-colors transition-opacity duration-300 border-none text-center"

const getVariantClasses = (variant: ButtonVariant) => {
  switch (variant) {
    case "inverted":
      return "font-nunito font-medium bg-surface text-fg-brand hover:bg-brand-muted focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
    case "hero":
      return "font-bold font-manrope bg-surface-paper text-brand hover:bg-brand-muted focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
    case "disabled":
      return "font-nunito font-medium bg-nelo-disabled-gray text-fg-inverted focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
    default:
      return "font-nunito font-medium bg-nelo-blue text-fg-inverted hover:bg-nelo-blue-pressed active:bg-nelo-blue-pressed focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
  }
}

const LoadingIndicator = ({ visible }: { visible: boolean }) => (
  <div className={`inline-block transition-all duration-300 ${visible ? "mr-2 w-4 opacity-100" : "mr-0 w-0 opacity-0"}`}>
    <Spinner />
  </div>
)

export const NeloButton = ({ children, onClick, variant = "primary", disabled = false, className = "", loading = false, ...props }: ButtonProps) => {
  const mergedClasses = twMerge(baseClasses, getVariantClasses(variant), className, "flex items-center justify-center", loading && "bg-nelo-blue-pressed text-fg-inverted")

  return (
    <button type="button" className={mergedClasses} style={{ WebkitTapHighlightColor: "transparent" }} onClick={onClick} disabled={disabled} {...props}>
      <LoadingIndicator visible={loading} />
      {children}
    </button>
  )
}
